import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, SlidersHorizontal, Timer, TrendingUp } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "../lib/api";
import { setMeta } from "../lib/seo";
import { useCountdown } from "../lib/useCountdown";
import { ProductCard } from "../components/ProductCard";
import { AdSlot } from "../components/AdSlot";

const SORTS = [
  { value: "best_sellers", label: "Best sellers" },
  { value: "trending", label: "Trending" },
  { value: "price_low", label: "Price: low to high" },
  { value: "price_high", label: "Price: high to low" },
  { value: "discount", label: "Biggest discount" },
];

const RATINGS = [
  { value: "0", label: "Any rating" },
  { value: "4", label: "4 stars & up" },
  { value: "4.5", label: "4.5 stars & up" },
];

export default function ListingPage({ mode }) {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q") || "";

  const [sort, setSort] = useState("best_sellers");
  const [minRating, setMinRating] = useState("0");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const countdown = useCountdown();

  const category = mode === "category" ? slug : undefined;
  const tag = mode === "deals" ? "deal" : mode === "trending" ? "trending" : undefined;

  useEffect(() => {
    setSelectedBrands([]);
    setMinRating("0");
    setMinPrice("");
    setMaxPrice("");
    setSort("best_sellers");
  }, [mode, slug, q]);

  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: api.getCategories });
  const currentCat = categories.find((c) => c.slug === slug);

  const { data: brands = [] } = useQuery({
    queryKey: ["brands", category || "all"],
    queryFn: () => api.getBrands(category),
  });

  const params = useMemo(() => {
    const p = { sort, limit: 60 };
    if (category) p.category = category;
    if (tag) p.tag = tag;
    if (mode === "search" && q) p.q = q;
    if (selectedBrands.length) p.brand = selectedBrands.join(",");
    if (minRating !== "0") p.min_rating = parseFloat(minRating);
    if (minPrice !== "") p.min_price = parseFloat(minPrice);
    if (maxPrice !== "") p.max_price = parseFloat(maxPrice);
    return p;
  }, [mode, category, tag, q, sort, minRating, minPrice, maxPrice, selectedBrands]);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products", params],
    queryFn: () => api.getProducts(params),
  });

  const title =
    mode === "category" ? currentCat?.name || "Category"
    : mode === "deals" ? "Daily Deals"
    : mode === "trending" ? "Trending Now"
    : `Search results for "${q}"`;

  useEffect(() => {
    setMeta(
      `${title} | ShoppinMagic`,
      mode === "category" && currentCat
        ? `${currentCat.description} Compare top-rated picks and prices at ShoppinMagic.`
        : `${title} — curated products and deals from trusted merchants at ShoppinMagic.`
    );
  }, [title, currentCat, mode]);

  const toggleBrand = (b) =>
    setSelectedBrands((prev) => (prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b]));

  const FilterBody = (
    <div className="space-y-6" data-testid="filters-panel">
      <div>
        <h3 className="mb-2 text-sm font-bold text-slate-900">Price range</h3>
        <div className="flex items-center gap-2">
          <input
            type="number" min="0" placeholder="Min" value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
            data-testid="filter-price-min"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-gold-500 focus:outline-none"
          />
          <span className="text-slate-400">–</span>
          <input
            type="number" min="0" placeholder="Max" value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            data-testid="filter-price-max"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-gold-500 focus:outline-none"
          />
        </div>
      </div>
      <div>
        <h3 className="mb-2 text-sm font-bold text-slate-900">Rating</h3>
        <div className="space-y-1.5">
          {RATINGS.map((r) => (
            <label key={r.value} className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
              <input
                type="radio" name="rating" checked={minRating === r.value}
                onChange={() => setMinRating(r.value)}
                data-testid={`filter-rating-${r.value.replace(".", "-")}`}
                className="accent-gold-600"
              />
              {r.label}
            </label>
          ))}
        </div>
      </div>
      <div>
        <h3 className="mb-2 text-sm font-bold text-slate-900">Brand</h3>
        <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
          {brands.map((b) => (
            <label key={b} className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox" checked={selectedBrands.includes(b)}
                onChange={() => toggleBrand(b)}
                data-testid={`filter-brand-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="accent-gold-600"
              />
              {b}
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid={`listing-${mode}`} className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <AdSlot slot="top-banner" minH="min-h-[60px]" className="mb-6" />

      <nav className="mb-3 flex items-center gap-1 text-xs text-slate-500" aria-label="Breadcrumb" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-gold-600">Home</Link>
        <ChevronRight size={12} />
        {mode === "category" && currentCat ? (
          <>
            <span>Categories</span>
            <ChevronRight size={12} />
            <span className="font-semibold text-slate-700">{currentCat.name}</span>
          </>
        ) : (
          <span className="font-semibold text-slate-700">{title}</span>
        )}
      </nav>

      <div className="mb-6">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
        {mode === "category" && currentCat && (
          <p className="mt-2 max-w-2xl text-sm text-slate-600">{currentCat.description}</p>
        )}
        {mode === "deals" && (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <p className="max-w-2xl text-sm text-slate-600">Today's biggest discounts across every category — refreshed daily at midnight.</p>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-gold-100 px-3 py-1 text-xs font-bold text-navy-900" data-testid="deals-countdown-chip">
              <Timer size={13} className="text-gold-700" /> Ends in <span className="font-mono">{countdown}</span>
            </span>
          </div>
        )}
        {mode === "trending" && (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <p className="max-w-2xl text-sm text-slate-600">The finds getting the most attention right now — straight from our curators.</p>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-navy-900 px-3 py-1 text-xs font-bold text-gold-400" data-testid="trending-live-chip">
              <TrendingUp size={13} /> Updated hourly
            </span>
          </div>
        )}
      </div>

      <div className="mb-4 flex items-center justify-between gap-3">
        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
          <SheetTrigger asChild>
            <button data-testid="open-filters-button" className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 lg:hidden">
              <SlidersHorizontal size={15} /> Filters
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80 overflow-y-auto bg-white">
            <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
            <div className="mt-4">{FilterBody}</div>
            <button
              data-testid="apply-filters-button"
              onClick={() => setFiltersOpen(false)}
              className="mt-6 w-full rounded-xl bg-gold-600 py-3 text-sm font-bold text-white hover:bg-gold-700"
            >
              Show {products.length} results
            </button>
          </SheetContent>
        </Sheet>
        <p className="text-xs text-slate-500" data-testid="results-count">{products.length} products</p>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="sort-select" className="w-48 bg-white">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {SORTS.map((s) => (
              <SelectItem key={s.value} value={s.value} data-testid={`sort-${s.value}`}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-8">
        <aside className="hidden w-56 shrink-0 lg:block">
          <div className="sticky top-24 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">{FilterBody}</div>
        </aside>
        <div className="flex-1">
          {isLoading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="aspect-[3/4] animate-pulse rounded-xl bg-slate-100" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="no-results">
              <p className="font-semibold text-slate-700">No products match those filters.</p>
              <p className="mt-1 text-sm text-slate-500">Try widening the price range or clearing brands.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 xl:grid-cols-4">
                {products.map((p) => <ProductCard key={p.slug} product={p} />)}
              </div>
              <AdSlot slot="mid-page" minH="min-h-[100px]" className="mt-8" />
            </>
          )}
        </div>
      </div>

      <AdSlot slot="footer" minH="min-h-[90px]" className="mt-8" />
    </div>
  );
}
